import React from "react";
import { Skeleton } from "@/components/ui/skeleton";

/** Placeholder rows rendered while the first page is in flight. */
const SKELETON_ROW_COUNT = 8;

interface TableRowsSkeletonProps {
  /** Number of visible columns, so the placeholder matches the real grid. */
  columnCount: number;
}

/**
 * Desktop loading placeholder for the CRM table body.
 *
 * Why this exists: the desktop body had NO loading state. With no rows yet and
 * the query still running, it fell straight through to `EmptyState` — so a
 * first load (contacts have measured 1.7–2.7s) told the user "No contacts
 * found" before their data arrived. Mobile already had
 * `SimpleTableMobileSkeleton`; this is its desktop counterpart.
 */
export const TableRowsSkeleton: React.FC<TableRowsSkeletonProps> = React.memo(
  ({ columnCount }) => {
    // Always draw at least a couple of columns so the placeholder reads as a
    // table even before column config resolves.
    const columns = Math.max(2, Math.min(columnCount, 8));

    return (
      <div
        className="divide-y divide-border/60"
        data-testid="crm-table-rows-skeleton"
        role="status"
        aria-live="polite"
        aria-label="Loading rows"
      >
        {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
          <div
            key={rowIndex}
            className="flex items-center gap-4 px-4 py-3"
            // Fade successive rows so the eye reads "more below" rather than a
            // solid block.
            style={{ opacity: 1 - rowIndex * 0.09 }}
          >
            {Array.from({ length: columns }).map((__, columnIndex) => (
              <Skeleton
                key={columnIndex}
                className={
                  columnIndex === 0
                    ? "h-4 w-[22%] min-w-32"
                    : columnIndex === columns - 1
                      ? "h-4 w-[10%] min-w-16"
                      : "h-4 w-[14%] min-w-20"
                }
              />
            ))}
          </div>
        ))}
        <span className="sr-only">Loading…</span>
      </div>
    );
  }
);

TableRowsSkeleton.displayName = "TableRowsSkeleton";
